const STATE_KEY = 'tah:state';
const STEP_COUNT = 3;
// Which step owns each field (0 = the scent picker above the form).
const FIELD_STEP = { scents: 0, name: 1, phone: 1, email: 1, address1: 1, address2: 1, pincode: 1, date: 2, slot: 2, consent: 3 };

const storage = {
  get(kind, key) {
    try { return window[kind].getItem(key); } catch (e) { return null; }
  },
  set(kind, key, value) {
    try { window[kind].setItem(key, value); } catch (e) { /* private mode */ }
  },
  remove(kind, key) {
    try { window[kind].removeItem(key); } catch (e) { /* private mode */ }
  }
};

const fill = (template, vars) =>
  String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) => (key in vars ? vars[key] : m));

const escapeHtml = (value) =>
  String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Shopify's `t` filter HTML-escapes translations (e.g. ' becomes &#39;), so
// decode them once when the strings block is read.
const decodeEntities = (value) => {
  if (Array.isArray(value)) return value.map(decodeEntities);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, decodeEntities(v)]));
  }
  if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
  const el = document.createElement('textarea');
  el.innerHTML = value;
  return el.value;
};

const SOUND_ICONS = {
  muted: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4V5z"/><path d="m22 9-6 6M16 9l6 6"/></svg>',
  unmuted: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4V5z"/><path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13"/></svg>'
};

const BOTTLE_ICON = '<svg class="hdt-tah-slot__ghost" viewBox="0 0 40 64" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" aria-hidden="true"><rect x="13" y="3" width="14" height="10" rx="2"/><path d="M16 13h8v6h-8z"/><rect x="7" y="19" width="26" height="42" rx="5"/></svg>';

const pad = (n) => String(n).padStart(2, '0');
const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

class HdtTryAtHome extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;

    this.strings = this.readStrings();
    this.locale = this.dataset.locale || document.documentElement.lang || 'en';
    const minDefault = Math.max(1, Number(this.dataset.minScents) || 1);
    const maxDefault = Math.max(minDefault, Number(this.dataset.maxScents) || minDefault);
    this.config = { min_scents: minDefault, max_scents: maxDefault, serviceable_pincodes: [] };
    this.days = [];
    this.dayMap = new Map();
    this.availability = 'idle'; // idle | loading | ready | error | none
    this.state = Object.assign({ scents: [], name: '', phone: '', email: '', address1: '', address2: '', pincode: '', date: '', slot: '', slotLabel: '', step: 1 }, this.loadState());
    this.view = null; // { y, m } month shown in the calendar

    this.cache();
    this.bind();
    this.restoreInputs();
    this.renderScents();
    this.renderBundle();
    this.renderReceipt();
    this.showStep(this.clampStep(this.state.step), { silent: true });
    this.loadAvailability().then(() => this.applyPreview());
    this.mountHeroSound();
  }

  /* ---------- preview aid: ?tah_preview=1|2|3|done fills sample data and jumps to that view ---------- */

  applyPreview() {
    const mode = new URLSearchParams(window.location.search).get('tah_preview');
    if (!mode) return;
    const titles = this.scentButtons.map((btn) => btn.dataset.title);
    const pins = this.config.serviceable_pincodes;
    Object.assign(this.state, {
      scents: titles.slice(0, this.config.max_scents),
      name: 'Sample Name',
      phone: '9876543210',
      email: 'sample@example.com',
      address1: '12 Sample Street',
      address2: 'Near the landmark',
      pincode: pins.length ? pins[0] : '400001'
    });
    const day = this.days.find((d) => d.slots.some((s) => s.available));
    const slot = day ? day.slots.find((s) => s.available) : null;
    if (day && slot) { this.state.date = day.date; this.state.slot = slot.time; this.state.slotLabel = slot.label; }
    this.restoreInputs();
    this.renderScents();
    this.renderBundle();
    this.renderReceipt();
    if (mode === 'done') {
      this.finish({ bookingId: 'TAH-0000', date: this.state.date, dateLabel: day ? day.label : '', slot: this.state.slotLabel });
      return;
    }
    const step = Number(mode);
    if (step >= 1 && step <= STEP_COUNT) this.showStep(step, { silent: true });
  }

  /* ---------- hero video sound toggle ---------- */

  mountHeroSound() {
    if (this.dataset.heroSound !== 'true') return;
    const main = document.getElementById('MainContent') || document.body;
    const video = main.querySelector('.hdt-video video, .hdt-video-2 video');
    if (!video) return;
    const host = video.closest('.hdt-media-overlap-content') || video.parentElement;
    if (!host || host.querySelector('.hdt-tah-sound')) return;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'hdt-tah-sound';
    const sync = () => {
      const muted = video.muted;
      btn.innerHTML = muted ? SOUND_ICONS.muted : SOUND_ICONS.unmuted;
      btn.setAttribute('aria-pressed', muted ? 'false' : 'true');
      btn.setAttribute('aria-label', muted ? this.t('ui.unmute') : this.t('ui.mute'));
    };
    btn.addEventListener('click', () => {
      video.muted = !video.muted;
      if (!video.muted) {
        video.volume = 1;
        video.play().catch(() => {});
      }
      sync();
    });
    video.addEventListener('volumechange', sync);
    sync();
    host.classList.add('hdt-tah-has-sound');
    host.appendChild(btn);
  }

  /* ---------- setup ---------- */

  readStrings() {
    const node = this.querySelector('[data-tah-strings]');
    try { return decodeEntities(JSON.parse(node ? node.textContent : '{}')); } catch (e) { return {}; }
  }

  get url() {
    return (this.dataset.url || '').trim();
  }

  t(path, vars) {
    const value = path.split('.').reduce((acc, key) => (acc && acc[key] != null ? acc[key] : undefined), this.strings);
    return vars ? fill(value, vars) : (value == null ? '' : value);
  }

  cache() {
    const q = (sel) => this.querySelector(sel);
    this.scentButtons = Array.from(this.querySelectorAll('[data-scent]'));
    this.scentData = new Map(this.scentButtons.map((btn) => [btn.dataset.title, { image: btn.dataset.image || '', notes: btn.dataset.notes || '' }]));
    this.scentHint = q('[data-scent-hint]');
    this.bundleSlots = q('[data-bundle-slots]');
    this.form = q('[data-form]');
    this.bookingWrap = q('[data-booking]');
    this.stepItems = Array.from(this.querySelectorAll('[data-step-ind]'));
    this.panels = Array.from(this.querySelectorAll('[data-panel]'));
    this.calendar = q('[data-calendar]');
    this.slotList = q('[data-slots]');
    this.slotDate = q('[data-slot-date]');
    this.summary = q('[data-summary]');
    this.consent = q('[name="consent"]');
    this.receipt = q('[data-receipt]');
    this.nextBtn = q('[data-next]');
    this.backBtn = q('[data-back]');
    this.status = q('[data-status]');
    this.alert = q('[data-alert]');
    this.alertText = q('[data-alert-text]');
    this.retryBtn = q('[data-retry]');
    this.done = q('[data-done]');
  }

  bind() {
    this.scentButtons.forEach((btn) => btn.addEventListener('click', () => this.toggleScent(btn.dataset.title)));

    this.form.addEventListener('submit', (e) => { e.preventDefault(); this.next(); });
    this.form.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON' || e.target.type === 'checkbox') return;
      e.preventDefault();
      this.next();
    });
    this.form.addEventListener('input', (e) => {
      const field = e.target.name;
      if (!(field in FIELD_STEP) || FIELD_STEP[field] !== 1) return;
      this.state[field] = e.target.value;
      this.setFieldError(field, '');
      this.saveState();
      this.renderReceipt();
    });
    this.form.addEventListener('change', (e) => {
      if (e.target.name === 'slot' && !e.target.disabled) {
        this.state.slot = e.target.value;
        this.state.slotLabel = e.target.dataset.label || e.target.value;
        this.setStatus('');
        this.saveState();
        this.renderReceipt();
      }
      if (e.target.name === 'consent') this.setFieldError('consent', '');
    });

    this.backBtn.addEventListener('click', () => this.back());
    this.retryBtn.addEventListener('click', () => this.retry());

    this.stepItems.forEach((item) => {
      const btn = item.querySelector('button');
      btn.addEventListener('click', () => {
        const target = Number(item.dataset.stepInd);
        if (target < this.state.step) this.showStep(target);
      });
    });

    this.calendar.addEventListener('click', (e) => {
      const nav = e.target.closest('[data-cal-nav]');
      if (nav && !nav.disabled) { this.moveMonth(Number(nav.dataset.calNav)); return; }
      const day = e.target.closest('[data-date]');
      if (day && !day.disabled) this.pickDate(day.dataset.date);
      const retry = e.target.closest('[data-cal-retry]');
      if (retry) this.loadAvailability(true);
    });

    this.summary.addEventListener('click', (e) => {
      const edit = e.target.closest('[data-goto]');
      if (edit) this.showStep(Number(edit.dataset.goto));
    });
  }

  /* ---------- state ---------- */

  loadState() {
    try { return JSON.parse(storage.get('sessionStorage', STATE_KEY) || '{}') || {}; } catch (e) { return {}; }
  }

  saveState() {
    storage.set('sessionStorage', STATE_KEY, JSON.stringify(this.state));
  }

  clearState() {
    storage.remove('sessionStorage', STATE_KEY);
  }

  clampStep(step) {
    const n = Number(step) || 1;
    return Math.min(STEP_COUNT, Math.max(1, n));
  }

  restoreInputs() {
    ['name', 'phone', 'email', 'address1', 'address2', 'pincode'].forEach((name) => {
      const input = this.form.elements[name];
      if (input && this.state[name]) input.value = this.state[name];
    });
  }

  /* ---------- scents + bundle ---------- */

  toggleScent(title) {
    const idx = this.state.scents.indexOf(title);
    if (idx > -1) {
      this.state.scents.splice(idx, 1);
      this.showScentHint('');
    } else if (this.state.scents.length >= this.config.max_scents) {
      this.showScentHint(this.t('ui.max_reached', { max: this.config.max_scents }), true);
      return;
    } else {
      this.state.scents.push(title);
      this.showScentHint('');
    }
    this.saveState();
    this.renderScents();
    this.renderBundle();
    this.renderReceipt();
  }

  showScentHint(message, isError) {
    const count = this.state.scents.length;
    const { min_scents: min, max_scents: max } = this.config;
    if (!message) {
      const base = min === max ? this.t('ui.pick_exact', { max }) : this.t('ui.pick_range', { min, max });
      message = `${base} · ${this.t('ui.selected_count', { count, max })}`;
      isError = false;
    }
    this.scentHint.textContent = message;
    this.scentHint.classList.toggle('is-error', !!isError);
  }

  renderScents() {
    this.scentButtons.forEach((btn) => {
      btn.setAttribute('aria-pressed', this.state.scents.includes(btn.dataset.title) ? 'true' : 'false');
    });
    this.showScentHint('');
  }

  renderBundle() {
    const max = this.config.max_scents;
    const html = [];
    for (let i = 0; i < max; i += 1) {
      const title = this.state.scents[i];
      const data = title ? this.scentData.get(title) : null;
      const label = title || this.t('ui.scent_n', { n: i + 1 });
      html.push(
        `<li class="hdt-tah-slot${title ? ' is-filled' : ''}">` +
          `<span class="hdt-tah-slot__circle">${data && data.image ? `<img src="${escapeHtml(data.image)}" alt="" loading="lazy" width="200" height="200">` : BOTTLE_ICON}</span>` +
          `<span class="hdt-tah-slot__name">${escapeHtml(label)}</span>` +
        '</li>'
      );
    }
    this.bundleSlots.innerHTML = html.join('');
  }

  /* ---------- availability ---------- */

  loadAvailability(force) {
    if (!this.url) {
      this.availability = 'none';
      this.renderCalendar();
      return Promise.resolve();
    }
    if (this.availability === 'loading') return this.pending;
    if (this.availability === 'ready' && !force) return Promise.resolve();

    // A refresh of already-loaded data happens quietly; the first load shows the loading state.
    const silent = this.availability === 'ready';
    if (!silent) {
      this.availability = 'loading';
      this.renderCalendar();
    }
    const endpoint = this.url + (this.url.includes('?') ? '&' : '?') + 'action=availability';

    this.pending = fetch(endpoint, { cache: 'no-store' })
      .then((res) => res.json())
      .then((data) => {
        if (!data || !data.ok) throw new Error('availability');
        this.days = Array.isArray(data.days) ? data.days : [];
        this.dayMap = new Map(this.days.map((d) => [d.date, d]));
        // The backend requires an exact number of scents (Config: scents_per_booking).
        const cfg0 = data.config || {};
        const exact = Number(cfg0.scents_per_booking) || this.config.max_scents;
        const min = Math.max(1, Number(cfg0.min_scents) || exact);
        const max = Math.max(min, Number(cfg0.max_scents) || exact);
        this.config.min_scents = min;
        this.config.max_scents = max;
        const count = max;
        // Script 1.3+ sends the list (and pincode_check). Older scripts send neither, so the
        // section's fallback list applies until the client updates the script.
        const cfg = data.config || {};
        const fromBackend = 'pincode_check' in cfg;
        const raw = fromBackend
          ? (Array.isArray(cfg.serviceable_pincodes) ? cfg.serviceable_pincodes : [])
          : String(this.dataset.pincodeFallback || '').split(',');
        this.config.serviceable_pincodes = raw.map((p) => String(p).replace(/\D/g, '')).filter((p) => p.length === 6);
        if (this.state.scents.length > count) this.state.scents.length = count;

        // Drop a saved date or slot that is no longer bookable.
        const day = this.dayMap.get(this.state.date);
        if (!day) { this.state.date = ''; this.state.slot = ''; this.state.slotLabel = ''; }
        else if (this.state.slot && !day.slots.some((s) => s.time === this.state.slot && s.available)) { this.state.slot = ''; this.state.slotLabel = ''; }
        this.saveState();
        this.availability = 'ready';
      })
      .catch(() => { if (!silent) this.availability = 'error'; })
      .then(() => {
        this.renderScents();
        this.renderBundle();
        this.renderReceipt();
        this.renderCalendar();
        if (this.state.step === 2) this.renderSlots();
        if (this.state.step === 3) this.renderSummary();
      });
    return this.pending;
  }

  /* ---------- calendar ---------- */

  monthOf(dateStr) {
    const [y, m] = dateStr.split('-').map(Number);
    return { y, m: m - 1 };
  }

  monthIndex({ y, m }) { return y * 12 + m; }

  renderCalendar() {
    if (this.availability !== 'ready') {
      const key = { loading: 'ui.loading', error: 'ui.unavailable', none: 'ui.not_configured', idle: 'ui.loading' }[this.availability];
      const busy = this.availability === 'loading' || this.availability === 'idle';
      const retry = this.availability === 'error'
        ? `<button type="button" class="hdt-btn hdt-btn-outline hdt-text-sm" data-cal-retry>${escapeHtml(this.t('ui.retry'))}</button>` : '';
      const text = busy
        ? `<span class="hdt-tah-spinner" aria-hidden="true"></span><span class="sr-only">${escapeHtml(this.t(key))}</span>`
        : `<span>${escapeHtml(this.t(key))}</span>`;
      this.calendar.innerHTML = `<div class="hdt-tah-cal__status" role="status"${busy ? ' aria-busy="true"' : ''}>${text}${retry}</div>`;
      return;
    }
    if (!this.days.length) {
      this.calendar.innerHTML = `<div class="hdt-tah-cal__status" role="status">${escapeHtml(this.t('ui.no_dates'))}</div>`;
      return;
    }

    const first = this.monthOf(this.days[0].date);
    const last = this.monthOf(this.days[this.days.length - 1].date);
    if (!this.view) this.view = this.state.date ? this.monthOf(this.state.date) : first;
    const idx = this.monthIndex(this.view);
    const { y, m } = this.view;

    const title = new Intl.DateTimeFormat(this.locale, { month: 'long', year: 'numeric' }).format(new Date(y, m, 1));
    const weekdays = String(this.t('ui.weekdays')).split(',');
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const offset = new Date(y, m, 1).getDay();

    let cells = weekdays.map((w) => `<span class="hdt-tah-cal__wd" aria-hidden="true">${escapeHtml(w.trim())}</span>`).join('');
    for (let i = 0; i < offset; i += 1) cells += '<span></span>';
    for (let d = 1; d <= daysInMonth; d += 1) {
      const date = ymd(y, m, d);
      const day = this.dayMap.get(date);
      const open = !!day && day.slots.some((s) => s.available);
      const selected = date === this.state.date;
      const label = day ? day.label : date;
      const hint = day && !open ? ` (${this.t('ui.fully_booked')})` : '';
      cells += `<button type="button" class="hdt-tah-day" data-date="${date}" aria-pressed="${selected}" aria-label="${escapeHtml(label + hint)}"${open ? '' : ' disabled'}>${d}</button>`;
    }

    const arrow = (dir) => `<svg viewBox="0 0 10 16" fill="none" aria-hidden="true"><path d="${dir < 0 ? 'M8 1 1 8l7 7' : 'M2 1l7 7-7 7'}" stroke="currentColor" stroke-width="1.6"/></svg>`;
    this.calendar.innerHTML =
      '<div class="hdt-tah-cal__head">' +
        `<button type="button" class="hdt-tah-cal__nav" data-cal-nav="-1" aria-label="${escapeHtml(this.t('ui.prev_month'))}"${idx <= this.monthIndex(first) ? ' disabled' : ''}>${arrow(-1)}</button>` +
        `<span class="hdt-tah-cal__title" aria-live="polite">${escapeHtml(title)}</span>` +
        `<button type="button" class="hdt-tah-cal__nav" data-cal-nav="1" aria-label="${escapeHtml(this.t('ui.next_month'))}"${idx >= this.monthIndex(last) ? ' disabled' : ''}>${arrow(1)}</button>` +
      '</div>' +
      `<div class="hdt-tah-cal__grid">${cells}</div>`;
  }

  moveMonth(delta) {
    if (!this.view) return;
    const m = this.view.m + delta;
    this.view = { y: this.view.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 };
    this.renderCalendar();
  }

  pickDate(date) {
    if (this.state.date !== date) {
      this.state.date = date;
      this.state.slot = '';
      this.state.slotLabel = '';
    }
    this.setStatus('');
    this.saveState();
    this.renderCalendar();
    this.renderSlots();
    this.renderReceipt();
  }

  /* ---------- slots ---------- */

  renderSlots() {
    const day = this.dayMap.get(this.state.date);
    this.slotDate.textContent = day ? day.label : '';
    if (!day) {
      this.slotList.innerHTML = `<li class="hdt-tah-times__empty">${escapeHtml(this.t('ui.pick_date_first'))}</li>`;
      return;
    }
    this.slotList.innerHTML = day.slots.map((slot, i) => {
      const id = `tah-slot-${i}`;
      const booked = !slot.available;
      const checked = slot.time === this.state.slot && !booked;
      return (
        `<li><label class="hdt-tah-time${booked ? ' is-booked' : ''}" for="${id}">` +
          `<span>${escapeHtml(slot.label || slot.time)}</span>` +
          `<input type="radio" id="${id}" name="slot" value="${escapeHtml(slot.time)}" data-label="${escapeHtml(slot.label || slot.time)}"${booked ? ' disabled' : ''}${checked ? ' checked' : ''}>` +
        '</label></li>'
      );
    }).join('');
  }

  /* ---------- summary + receipt ---------- */

  addressLine() {
    const s = this.state;
    return [s.address1, s.address2, this.dataset.city, this.dataset.state, s.pincode]
      .map((v) => String(v || '').trim()).filter(Boolean).join(', ');
  }

  pincodeError(pin) {
    if (!/^\d{6}$/.test(pin)) return this.t('errors.pincode');
    const list = this.config.serviceable_pincodes;
    if (list.length && !list.includes(pin)) return this.t('errors.pincode_area');
    return '';
  }

  whenLine() {
    const day = this.dayMap.get(this.state.date);
    const dateLabel = day ? day.label : this.state.date;
    return [dateLabel, this.state.slotLabel].filter(Boolean).join(' · ');
  }

  renderReceipt() {
    const r = this.receipt;
    const set = (sel, value, placeholder) => {
      const el = r.querySelector(sel);
      if (!el) return;
      el.textContent = value || placeholder;
      el.classList.toggle('is-placeholder', !value);
    };
    set('[data-r-name]', String(this.state.name || '').trim(), this.t('receipt.name'));
    set('[data-r-address]', this.addressLine(), this.t('receipt.address'));
    set('[data-r-time]', this.whenLine(), this.t('receipt.time_placeholder'));

    r.querySelector('[data-r-scents]').innerHTML = this.scentLines(true);
  }

  scentLines(withPlaceholders) {
    const lines = [];
    const count = withPlaceholders ? this.config.max_scents : this.state.scents.length;
    for (let i = 0; i < count; i += 1) {
      const title = this.state.scents[i];
      const data = title ? this.scentData.get(title) : null;
      lines.push(
        `<li${title ? '' : ' class="is-placeholder"'}><span>` +
          `<span class="hdt-tah-receipt__title">${escapeHtml(title || this.t('ui.scent_n', { n: i + 1 }))}</span>` +
          (data && data.notes ? `<span class="hdt-tah-receipt__sub">${escapeHtml(data.notes)}</span>` : '') +
        '</span></li>'
      );
    }
    return lines.join('');
  }

  renderSummary() {
    const row = (label, value, step) =>
      `<div class="hdt-tah-summary__row"><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value || '—')}</dd>` +
      `<dd><button type="button" class="hdt-tah-summary__edit" data-goto="${step}">${escapeHtml(this.t('ui.edit'))}</button></dd></div>`;
    this.summary.innerHTML =
      row(this.t('summary.name'), this.state.name, 1) +
      row(this.t('summary.phone'), this.state.phone ? `${this.t('fields.phone_prefix')} ${this.normalisePhone(this.state.phone) || this.state.phone}` : '', 1) +
      row(this.t('summary.email'), this.state.email, 1) +
      row(this.t('summary.address'), this.addressLine(), 1) +
      row(this.t('summary.scents'), this.state.scents.join(', '), 0) +
      row(this.t('summary.when'), this.whenLine(), 2);
  }

  /* ---------- steps ---------- */

  showStep(step, opts = {}) {
    step = this.clampStep(step);
    if (step === 0) step = 1;
    this.state.step = step;
    this.saveState();
    this.hideAlert();
    this.setStatus('');

    this.panels.forEach((panel) => {
      const isActive = Number(panel.dataset.panel) === step;
      panel.hidden = !isActive;
      panel.classList.remove('is-entering');
      if (isActive && !opts.silent) {
        panel.classList.add('is-entering');
        panel.addEventListener('animationend', () => panel.classList.remove('is-entering'), { once: true });
      }
    });

    this.stepItems.forEach((item) => {
      const n = Number(item.dataset.stepInd);
      item.classList.toggle('is-done', n < step);
      item.classList.toggle('is-current', n === step);
      const btn = item.querySelector('button');
      if (n === step) btn.setAttribute('aria-current', 'step'); else btn.removeAttribute('aria-current');
      btn.disabled = n > step;
    });

    this.backBtn.hidden = step === 1;
    this.nextBtn.textContent = step === STEP_COUNT ? this.nextBtn.dataset.confirmLabel : this.nextBtn.dataset.nextLabel;

    if (step === 2) {
      this.view = this.state.date ? this.monthOf(this.state.date) : null;
      this.renderCalendar();
      this.renderSlots();
      this.loadAvailability(true);
    }
    if (step === 3) this.renderSummary();

    if (!opts.silent) {
      const panel = this.panels.find((p) => Number(p.dataset.panel) === step);
      const title = panel && panel.querySelector('.hdt-tah-panel__title');
      if (title) title.focus({ preventScroll: true });
      const top = this.bookingWrap.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: Math.max(0, top), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }

  next() {
    const step = this.state.step;
    if (step === 1 && !(this.validateScents() && this.validateStep1())) return;
    if (step === 2 && !this.state.date) { this.setStatus(this.t('errors.date'), true); return; }
    if (step === 2 && !this.state.slot) { this.setStatus(this.t('errors.slot'), true); return; }
    if (step === 3) { this.submit(); return; }
    this.showStep(step + 1);
  }

  back() {
    if (this.state.step > 1) this.showStep(this.state.step - 1);
  }

  goToField(field) {
    const step = FIELD_STEP[field];
    if (step === 0) {
      const { min_scents: min, max_scents: max } = this.config;
      this.showScentHint(this.t('errors.' + (min === max ? 'scents_exact' : 'scents_range'), { min, max }), true);
      this.scentHint.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    if (step && step !== this.state.step) this.showStep(step);
  }

  /* ---------- validation ---------- */

  normalisePhone(raw) {
    let digits = String(raw || '').replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
    if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
    return /^[6-9]\d{9}$/.test(digits) ? digits : '';
  }

  validateScents() {
    const count = this.state.scents.length;
    const { min_scents: min, max_scents: max } = this.config;
    if (count >= min && count <= max) return true;
    this.goToField('scents');
    this.setStatus(this.t('errors.' + (min === max ? 'scents_exact' : 'scents_range'), { min, max }), true);
    return false;
  }

  validateStep1() {
    const errors = {};
    const s = this.state;
    if (String(s.name || '').trim().length < 2) errors.name = this.t('errors.name');
    if (!this.normalisePhone(s.phone)) errors.phone = this.t('errors.phone');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(s.email || '').trim())) errors.email = this.t('errors.email');
    if (String(s.address1 || '').trim().length < 3) errors.address1 = this.t('errors.address1');
    const pinError = this.pincodeError(String(s.pincode || '').replace(/\D/g, ''));
    if (pinError) errors.pincode = pinError;

    ['name', 'phone', 'email', 'address1', 'pincode'].forEach((f) => this.setFieldError(f, errors[f] || ''));
    const first = Object.keys(errors)[0];
    if (first) {
      const input = this.form.elements[first];
      if (input) input.focus();
      return false;
    }
    return true;
  }

  setFieldError(field, message) {
    const wrap = this.form.querySelector(`[data-field="${field}"]`);
    if (!wrap) return;
    const out = wrap.querySelector('[data-error]');
    if (out) out.textContent = message || '';
    wrap.classList.toggle('is-invalid', !!message);
    const input = wrap.querySelector('input, textarea');
    if (input) {
      if (message) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid');
    }
  }

  /* ---------- messages ---------- */

  setStatus(message, isError) {
    this.status.textContent = message || '';
    this.status.classList.toggle('is-error', !!(message && isError));
  }

  showAlert(message, canRetry) {
    this.alertText.textContent = message;
    this.retryBtn.hidden = !canRetry;
    this.alert.hidden = false;
  }

  hideAlert() { this.alert.hidden = true; }

  retry() {
    this.hideAlert();
    if (this.state.step === STEP_COUNT) this.submit(); else this.loadAvailability(true);
  }

  setBusy(busy) {
    this.nextBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
    this.nextBtn.disabled = busy;
    this.backBtn.disabled = busy;
    this.nextBtn.textContent = busy ? this.t('ui.sending') : this.nextBtn.dataset.confirmLabel;
  }

  /* ---------- submit ---------- */

  async submit() {
    if (this.consent && !this.consent.checked) {
      this.setFieldError('consent', this.t('errors.consent'));
      this.consent.focus();
      return;
    }
    if (!this.validateScents() || !this.validateStep1()) return;
    if (!this.state.date) { this.showStep(2); this.setStatus(this.t('errors.date'), true); return; }
    if (!this.state.slot) { this.showStep(2); this.setStatus(this.t('errors.slot'), true); return; }
    if (!this.url) { this.showAlert(this.t('ui.not_configured'), false); return; }

    const payload = {
      action: 'book',
      name: String(this.state.name).trim(),
      phone: this.normalisePhone(this.state.phone),
      email: String(this.state.email).trim(),
      address1: String(this.state.address1 || '').trim(),
      address2: String(this.state.address2 || '').trim(),
      city: String(this.dataset.city || '').trim(),
      state: String(this.dataset.state || '').trim(),
      pincode: String(this.state.pincode || '').replace(/\D/g, ''),
      address: this.addressLine(),
      scents: this.state.scents.slice(),
      date: this.state.date,
      slot: this.state.slot
    };

    this.hideAlert();
    this.setBusy(true);
    try {
      // Body is a JSON string with no Content-Type so the browser sends text/plain
      // and skips the CORS preflight that Apps Script cannot answer.
      const res = await fetch(this.url, { method: 'POST', body: JSON.stringify(payload) });
      const data = await res.json();
      if (data && data.ok) { this.finish(data); return; }

      const error = data && data.error;
      if (error === 'validation') {
        const fields = (data.fields && typeof data.fields === 'object') ? data.fields : {};
        const names = Object.keys(fields);
        names.forEach((f) => this.setFieldError(f, String(fields[f])));
        const owner = names.map((f) => FIELD_STEP[f]).filter((n) => n !== undefined).sort((a, b) => a - b)[0];
        if (owner === 0) { this.showScentHint(String(fields.scents), true); this.showStep(1); this.goToField('scents'); }
        else if (owner !== undefined) { this.showStep(owner); if (owner === 2) this.setStatus(String(fields.date || fields.slot), true); }
        else this.showAlert(data.message || this.t('errors.server'), false);
        return;
      }
      if (error === 'slot_taken') {
        this.state.slot = '';
        this.state.slotLabel = '';
        await this.loadAvailability(true);
        this.showStep(2);
        this.setStatus(data.message || this.t('errors.slot_taken'), true);
        return;
      }
      this.showAlert((data && data.message) || this.t('errors.server'), true);
    } catch (e) {
      this.showAlert(this.t('errors.network'), true);
    } finally {
      this.setBusy(false);
      if (this.state.step === STEP_COUNT) this.nextBtn.textContent = this.nextBtn.dataset.confirmLabel;
      else this.nextBtn.textContent = this.nextBtn.dataset.nextLabel;
    }
  }

  finish(data) {
    this.clearState();
    const d = this.done;
    d.querySelector('[data-done-id]').textContent = data.bookingId || '';
    d.querySelector('[data-done-name]').textContent = String(this.state.name || '').trim();
    d.querySelector('[data-done-address]').textContent = this.addressLine();
    d.querySelector('[data-done-when]').textContent = [data.dateLabel || data.date, data.slot].filter(Boolean).join(' · ');
    d.querySelector('[data-done-scents]').innerHTML = this.scentLines(false);
    const bundle = this.querySelector('.hdt-tah-bundle');
    const pair = d.querySelector('[data-done-pair]');
    if (bundle && pair && !pair.contains(bundle)) pair.prepend(bundle);
    this.bookingWrap.hidden = true;
    this.classList.add('is-done');
    d.hidden = false;
    const title = d.querySelector('[tabindex="-1"]');
    if (title) title.focus({ preventScroll: true });
    d.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('hdt-try-at-home')) customElements.define('hdt-try-at-home', HdtTryAtHome);
